import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Users, Calendar, DollarSign, Shield, Zap, Heart, ArrowRight } from "lucide-react";
import dashboardImg from "@/assets/dashboard-screenshot.png";

const LandingOption2 = () => {
  const features = [
    {
      title: "Member Management",
      description: "Efficiently manage PTA members, roles, and permissions",
      icon: Users,
    },
    {
      title: "Event Planning",
      description: "Organize events with intuitive calendar tools",
      icon: Calendar,
    },
    {
      title: "Fundraising Tools",
      description: "Track donations and manage campaigns",
      icon: DollarSign,
    },
    {
      title: "Security First",
      description: "GDPR compliant with robust protection",
      icon: Shield,
    },
    {
      title: "Easy Integration",
      description: "Connect with Stripe, Zoom, and more",
      icon: Zap,
    },
    {
      title: "Community Focused",
      description: "Built specifically for school PTAs",
      icon: Heart,
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section - Feature-First */}
      <section className="relative bg-background py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-6 mb-16 animate-fade-in">
            <h1 className="text-5xl md:text-7xl font-bold text-foreground">
              <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                Everything Your PTA Needs
              </span>
              <span className="block mt-2">In One Place</span>
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">
              The complete platform for modern school PTAs
            </p>
          </div>

          {/* Feature Grid - Immediately Visible */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card 
                  key={index} 
                  className="shadow-card hover:shadow-elevated transition-all border-2 hover:border-primary/20"
                >
                  <CardHeader>
                    <div className="space-y-4">
                      <div className="w-14 h-14 rounded-xl bg-gradient-primary flex items-center justify-center">
                        <Icon className="h-7 w-7 text-primary-foreground" />
                      </div>
                      <CardTitle className="text-xl">{feature.title}</CardTitle>
                      <CardDescription className="text-base">{feature.description}</CardDescription>
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>

          <div className="text-center space-y-4">
            <Link to="/auth">
              <Button size="lg" className="shadow-elevated text-lg px-10 py-6">
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <p className="text-sm text-muted-foreground">
              No credit card required • 14-day free trial
            </p>
          </div>
        </div>
      </section>

      {/* Dashboard Preview */}
      <section className="py-24 px-4 bg-card/30">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-6 mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground">
              Beautifully Simple, Powerfully Effective
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              An intuitive platform that your entire PTA team will love
            </p>
          </div>
          <Card className="overflow-hidden shadow-elevated">
            <img src={dashboardImg} alt="PTA Hub Dashboard" className="w-full" />
          </Card>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-24 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="grid md:grid-cols-3 gap-12 text-center">
            <div className="space-y-4">
              <div className="text-6xl font-bold text-primary">500+</div>
              <div className="text-xl text-foreground font-semibold">PTAs Trust Us</div>
              <p className="text-muted-foreground">
                Join hundreds of schools using PTA Hub
              </p>
            </div>
            <div className="space-y-4">
              <div className="text-6xl font-bold text-primary">10+</div>
              <div className="text-xl text-foreground font-semibold">Hours Saved Weekly</div>
              <p className="text-muted-foreground">
                More time for what matters most
              </p>
            </div>
            <div className="space-y-4">
              <div className="text-6xl font-bold text-primary">24/7</div>
              <div className="text-xl text-foreground font-semibold">Support Available</div>
              <p className="text-muted-foreground">
                We're here whenever you need us
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-gradient-hero">
        <div className="container mx-auto max-w-4xl text-center space-y-8">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground">
            Transform Your PTA Today
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Start managing your PTA more efficiently with our 14-day free trial
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/auth">
              <Button size="lg" className="shadow-elevated text-lg px-10 py-6">
                Get Started Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="text-lg px-10 py-6">
                View Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Label */}
      <div className="fixed top-20 right-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg shadow-lg font-bold z-50">
        Option 2
      </div>
    </div>
  );
};

export default LandingOption2;
